import React, { FC } from 'react';
import { IoRefreshOutline } from 'react-icons/io5';

import { Anchor, Box, Flex, Group, MantineStyleProps, Stack, Text } from '@mantine/core';

import { ProjectLimitTracker } from 'components/project-overview/components/ProjectLimitTracker';
import { ProjectSearchResultsProps } from 'components/project-search/ProjectSearch.Results';
import { ProjectTeaserHorizontal } from 'components/project-teaser/ProjectTeaserHorizontal';
import { ProjectTeaserHorizontalSkeleton } from 'components/project-teaser/ProjectTeaserHorizontalSkeleton';

import cx from './ProjectList.module.scss';

const ProjectList: FC<
    ProjectSearchResultsProps & {
        hideLimitTracker?: boolean;
        highlightTeam?: boolean;
        showLatestMessage?: boolean;
        hideMeta?: boolean;
        emptyLabel?: React.ReactNode;
    }
> = ({
    projects,
    isLoading = false,
    ScrollRef,
    loadedCount,
    totalCount,
    hasNextPage,
    forceLoadMore,
    hideLimitTracker,
    highlightTeam,
    showLatestMessage,
    hideMeta,
    emptyLabel = 'No projects found',
}) => {
    return (
        <Box style={{ width: '100%' }}>
            <Stack gap="md">
                {!hideLimitTracker && <ProjectLimitTracker />}

                {!!projects.length && (
                    <Box className={cx.list}>
                        {projects.map((project) => (
                            <ProjectTeaserHorizontal
                                key={project.id}
                                project={project}
                                highlightTeam={highlightTeam}
                                showLatestMessage={showLatestMessage}
                                hideMeta={hideMeta}
                            />
                        ))}

                        {((ScrollRef && hasNextPage) || isLoading) && (
                            <Box pos="relative">
                                <Box w="100%" pos="absolute" style={{ zIndex: 1, pointerEvents: 'none' }}>
                                    {ScrollRef && hasNextPage && <>{ScrollRef}</>}
                                </Box>
                                <ProjectTeaserHorizontalSkeleton />
                            </Box>
                        )}
                    </Box>
                )}

                {projects.length === 0 && isLoading && <ProjectListSkeleton />}

                {projects.length === 0 && !isLoading && (
                    <Stack
                        align="center"
                        justify="center"
                        gap="md"
                        p="xl"
                        style={{
                            border: '2px dashed var(--mantine-color-gray-1)',
                            borderRadius: 'var(--mantine-radius-md)',
                            minHeight: 200,
                        }}
                    >
                        <Text c="dimmed" size="sm" ta="center">
                            {emptyLabel}
                        </Text>
                    </Stack>
                )}

                {!!totalCount && (
                    <Group gap="xs" mt="md">
                        <Text c="dimmed" size="sm">
                            {loadedCount}/{totalCount} projects loaded
                        </Text>
                        {loadedCount < totalCount && (
                            <Anchor size="sm" variant="subtle" onClick={forceLoadMore}>
                                <IoRefreshOutline style={{ marginBottom: -2, marginRight: 4 }} /> Force load more
                            </Anchor>
                        )}
                    </Group>
                )}
            </Stack>
        </Box>
    );
};

const ProjectListSkeleton: FC = () => (
    <Box className={cx.list}>
        <ProjectTeaserHorizontalSkeleton />
        <ProjectTeaserHorizontalSkeleton />
        <ProjectTeaserHorizontalSkeleton />
    </Box>
);

export const ProjectMetaItem: FC<{
    icon?: React.ReactNode;
    label: React.ReactNode;
    emptyLabel?: React.ReactNode;
    c?: MantineStyleProps['c'];
    afterIcon?: React.ReactNode;
    isLoading?: boolean;
}> = ({ icon, label, emptyLabel, c = 'default', afterIcon, isLoading = false }) => {
    return (
        <Group gap="xs" align="center" c={c} wrap="nowrap">
            {icon && <Flex style={{ flexShrink: 0 }}>{icon}</Flex>}
            <Text
                style={{ lineHeight: '1rem' }}
                fz="sm"
                inherit
                component="div"
                c={label && !isLoading ? 'gray.6' : 'gray.4'}
            >
                {isLoading ? 'loading...' : label || emptyLabel}
            </Text>
            {afterIcon && (
                <Flex style={{ flexShrink: 0 }} ml={-6}>
                    {afterIcon}
                </Flex>
            )}
        </Group>
    );
};

export { ProjectList };
